import { useState, useEffect, useCallback, useRef } from "react";
import { Upload, Loader2, FileText, Sparkles, CheckCircle, Trash2, Eye, Camera, Package, Repeat } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import api from "../../lib/api";
import DuplicatesBanner from "./DuplicatesBanner";
import DuplicatesPreventedWidget from "./DuplicatesPreventedWidget";
import SyncToInventoryDialog from "./SyncToInventoryDialog";
import BulkInvoiceUploadDialog from "./BulkInvoiceUploadDialog";
import InvoiceImpactPanel from "./InvoiceImpactPanel";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES"); } catch { return iso; }
};

export default function DocumentsInbox({ restaurantId, categories, catLabels, onChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pendingDup, setPendingDup] = useState(null); // { file, duplicate }
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [pendingReceiptsTotal, setPendingReceiptsTotal] = useState(0);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const d = await api.listFinanceDocuments(restaurantId);
      setDocs(d || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [restaurantId]);

  const loadPendingReceipts = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const r = await api.getPendingReceipts(restaurantId, 30);
      setPendingReceipts(r?.pending || []);
      setPendingReceiptsTotal(r?.total_amount || 0);
    } catch { /* silent */ }
  }, [restaurantId]);

  useEffect(() => { load(); loadPendingReceipts(); }, [load, loadPendingReceipts]);

  const handleUpload = async (file, { force = false } = {}) => {
    if (!file) return;
    setUploading(true);
    toast.info("Escaneando con VirusTotal + IA…", { duration: 4000 });
    try {
      const res = await api.uploadFinanceDocument(restaurantId, file, { force });
      if (res?.auto_sync_scheduled) {
        toast.success(
          `${file.name} subido · añadiendo ${res.items_detected} productos al inventario automáticamente`,
          { duration: 6000 },
        );
      } else {
        toast.success(`${file.name} subido. Revisa los datos extraídos.`);
      }
      await load();
      onChange?.();
      // Auto-open review dialog
      const fresh = await api.getFinanceDocument(restaurantId, res.document_id);
      setActiveDoc(fresh);
    } catch (err) {
      if (err?.code === "duplicate_document") {
        setPendingDup({ file, duplicate: err.duplicate });
      } else {
        toast.error(err.message || "Error al subir");
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDelete = async (doc) => {
    try {
      await api.deleteFinanceDocument(restaurantId, doc.document_id);
      toast.success("Documento eliminado");
      setConfirmDel(null);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.message || "Error al eliminar");
    }
  };

  const pending = docs.filter(d => d.status === "pending_review");
  const processed = docs.filter(d => d.status === "processed");

  return (
    <div className="space-y-5" data-testid="documents-inbox">
      {/* "Ahorro detectado": duplicates the system prevented */}
      <DuplicatesPreventedWidget restaurantId={restaurantId} refreshKey={docs.length} />

      {/* Pending receipts from cash payouts — proactive reminder */}
      <PendingReceiptsWidget
        items={pendingReceipts}
        totalAmount={pendingReceiptsTotal}
        onPickFile={(payout) => {
          if (!fileRef.current) return;
          fileRef.current.dataset.expectedPayout = JSON.stringify(payout);
          fileRef.current.click();
        }}
      />

      {/* Duplicate-invoices banner (self-hides if none detected) */}
      <DuplicatesBanner restaurantId={restaurantId} onResolved={() => { load(); onChange?.(); }} />

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className="rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 p-6 text-center"
        data-testid="upload-dropzone"
      >
        <Upload className="h-8 w-8 mx-auto text-primary/70 mb-2" />
        <p className="text-sm text-muted-foreground mb-3">
          <span className="hidden sm:inline">Arrastra una factura, ticket o gasto aquí — </span>
          <span className="sm:hidden">Haz una foto a tu factura o ticket — </span>
          la IA extraerá los datos y VirusTotal escaneará el archivo.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          className="hidden"
          data-testid="upload-input"
        />
        {/* Mobile-camera input: opens the rear camera directly via capture="environment" */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          className="hidden"
          data-testid="camera-input"
        />
        <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-button"
            className="w-full sm:w-auto"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading ? "Subiendo y analizando…" : "Subir documento"}
          </Button>
          {/* "Hacer foto" — mobile + small tablet only (hidden on desktop ≥768px) */}
          <div className="w-full sm:w-auto md:hidden">
            <Button
              type="button"
              variant="outline"
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              data-testid="camera-button"
              className="w-full sm:w-auto"
            >
              <Camera className="h-4 w-4 mr-2" />
              Hacer foto
            </Button>
          </div>
          {/* Bulk uploader — historical imports */}
          <div className="w-full sm:w-auto">
            <BulkInvoiceUploadDialog
              restaurantId={restaurantId}
              onImported={() => { load(); onChange?.(); }}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {pending.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-amber-800 mb-2">
                Por revisar ({pending.length})
              </h3>
              <div className="space-y-2">
                {pending.map(d => (
                  <DocRow key={d.document_id}
                    doc={d} catLabels={catLabels}
                    onOpen={() => setActiveDoc(d)}
                    onDelete={() => setConfirmDel(d)}
                    pending
                  />
                ))}
              </div>
            </section>
          )}

          {processed.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                Procesados ({processed.length})
              </h3>
              <div className="space-y-2">
                {processed.map(d => (
                  <DocRow key={d.document_id}
                    doc={d} catLabels={catLabels}
                    onOpen={() => setActiveDoc(d)}
                    onDelete={() => setConfirmDel(d)}
                  />
                ))}
              </div>
            </section>
          )}

          {!pending.length && !processed.length && (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-sm text-muted-foreground">
              Aún no has subido ningún documento. Sube la primera factura y la IA hará el resto.
            </CardContent></Card>
          )}
        </>
      )}

      {activeDoc && (
        <DocumentReviewDialog
          doc={activeDoc}
          open={!!activeDoc}
          onClose={() => setActiveDoc(null)}
          restaurantId={restaurantId}
          categories={categories}
          onSaved={async () => { await load(); await loadPendingReceipts(); onChange?.(); }}
        />
      )}

      {confirmDel && (
        <AlertDialog open={!!confirmDel} onOpenChange={() => setConfirmDel(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminará <strong>{confirmDel.filename}</strong> y cualquier gasto vinculado.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700"
                onClick={(e) => { e.preventDefault(); handleDelete(confirmDel); }}>
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {pendingDup && (
        <AlertDialog open={!!pendingDup} onOpenChange={() => setPendingDup(null)}>
          <AlertDialogContent data-testid="duplicate-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>⚠️ Parece que ya tenías esta factura cargada</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>¿Seguro que quieres añadirla de nuevo? Se podría duplicar el gasto en tu P&amp;L.</p>
                  {pendingDup.duplicate && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-amber-900">
                      <div className="font-medium">
                        {pendingDup.duplicate.supplier_name || pendingDup.duplicate.filename || "Documento existente"}
                      </div>
                      <div className="text-xs mt-1 space-y-0.5">
                        {pendingDup.duplicate.total_amount != null && (
                          <div>Importe: <strong>{fmtEUR(pendingDup.duplicate.total_amount)}</strong></div>
                        )}
                        {pendingDup.duplicate.issue_date && (
                          <div>Fecha: <strong>{fmtDate(pendingDup.duplicate.issue_date)}</strong></div>
                        )}
                        {pendingDup.duplicate.invoice_number && (
                          <div>Nº factura: <strong>{pendingDup.duplicate.invoice_number}</strong></div>
                        )}
                        {pendingDup.duplicate.reason && (
                          <div className="italic opacity-80">{pendingDup.duplicate.reason}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="duplicate-cancel-btn">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                data-testid="duplicate-confirm-btn"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={(e) => {
                  e.preventDefault();
                  const { file } = pendingDup;
                  setPendingDup(null);
                  handleUpload(file, { force: true });
                }}>
                Sí, añadir igualmente
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

function DocRow({ doc, catLabels, onOpen, onDelete, pending }) {
  const conf = doc.ocr_confidence || 0;
  const low = conf < 0.8 && pending;
  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition ${pending ? "border-amber-300 bg-amber-50/40" : ""}`}
      onClick={onOpen}
      data-testid={`doc-row-${doc.document_id}`}
    >
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${pending ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
          {pending ? <FileText className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground truncate">
              {doc.supplier_name || doc.filename || "Sin proveedor"}
            </span>
            {doc.category && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-card border border-border text-foreground">
                {catLabels[doc.category] || doc.category}
              </span>
            )}
            {low && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 border border-orange-200 text-orange-800">
                <Sparkles className="h-3 w-3" /> IA {(conf * 100).toFixed(0)}%
              </span>
            )}
            {doc.paid && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                Pagado
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {fmtDate(doc.issue_date)} · {doc.filename}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-sm font-semibold ${doc.total_amount < 0 ? "text-red-700" : "text-foreground"}`}>
            {doc.total_amount != null ? fmtEUR(doc.total_amount) : "—"}
          </div>
          <div className="text-[10px] text-muted-foreground">{doc.currency || "EUR"}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-muted-foreground hover:text-red-700 p-1"
          aria-label="Eliminar"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </CardContent>
    </Card>
  );
}

function DocumentReviewDialog({ doc, open, onClose, restaurantId, categories, onSaved }) {
  const [form, setForm] = useState(() => ({
    supplier_name: doc.supplier_name || "",
    issue_date: doc.issue_date || "",
    due_date: doc.due_date || "",
    total_amount: doc.total_amount ?? "",
    tax_amount: doc.tax_amount ?? "",
    category: doc.category || "other",
    doc_type: doc.doc_type || "expense",
    notes: doc.notes || "",
    paid: !!doc.paid,
    is_recurring: false,
    recurring_cadence: "monthly",
    recurring_day: "",
  }));
  const [saving, setSaving] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);

  const fileUrl = api.financeDocumentFileUrl(restaurantId, doc.document_id);
  const isPdf = (doc.mime_type || "").toLowerCase().includes("pdf") ||
    (doc.filename || "").toLowerCase().endsWith(".pdf");
  const pageCount = Math.max(1, Number(doc.page_count) || 1);
  const previewUrl = isPdf
    ? api.financeDocumentPreviewUrl(restaurantId, doc.document_id, previewPage)
    : fileUrl;
  const items = (doc.ocr_raw && doc.ocr_raw.items) || [];
  const canSync = items.length > 0;
  const alreadySynced = !!doc.inventory_synced_at;

  const save = async (status = "processed") => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_amount: form.total_amount === "" ? null : Number(form.total_amount),
        tax_amount: form.tax_amount === "" ? null : Number(form.tax_amount),
        status,
        mark_paid: form.paid && !doc.paid,
        recurring_day: form.is_recurring
          ? Number(form.recurring_day || (form.issue_date ? form.issue_date.slice(8, 10) : 1))
          : null,
        recurring_cadence: form.is_recurring ? form.recurring_cadence : null,
      };
      await api.updateFinanceDocument(restaurantId, doc.document_id, payload);
      toast.success(status === "processed" ? "Documento procesado" : "Guardado");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const [reanalyzing, setReanalyzing] = useState(false);
  const [linkingPayout, setLinkingPayout] = useState(false);
  const [payoutHint, setPayoutHint] = useState(doc.suggested_cash_payout || null);
  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      const corrections = {
        supplier_name: form.supplier_name || null,
        issue_date: form.issue_date || null,
        due_date: form.due_date || null,
        total_amount: form.total_amount === "" ? null : Number(form.total_amount),
        tax_amount: form.tax_amount === "" ? null : Number(form.tax_amount),
        category: form.category || null,
      };
      const res = await api.reanalyzeFinanceDocument(
        restaurantId, doc.document_id, corrections,
      );
      const newOcr = res?.ocr || {};
      // Update the form fields with the reanalyzed values.
      setForm(f => ({
        ...f,
        supplier_name: newOcr.supplier_name || f.supplier_name,
        issue_date: newOcr.issue_date || f.issue_date,
        due_date: newOcr.due_date || f.due_date,
        total_amount: newOcr.total_amount ?? f.total_amount,
        tax_amount: newOcr.tax_amount ?? f.tax_amount,
        category: newOcr.category || f.category,
        notes: newOcr.notes || f.notes,
      }));
      const learned = !!res?.hint_saved;
      const applied = Number(res?.hints_applied || 0);
      toast.success(
        learned
          ? `Aprendido el patrón de ${res.hint_saved.supplier_name}. Reanálisis con ${applied} pista(s) aplicada(s).`
          : `Reanálisis completado con ${applied} pista(s) aplicada(s).`
      );
    } catch (err) {
      toast.error(err.message || "No se pudo reanalizar");
    } finally {
      setReanalyzing(false);
    }
  };

  const linkPayout = async () => {
    if (!payoutHint) return;
    setLinkingPayout(true);
    try {
      await api.linkDocumentToCashPayout(restaurantId, doc.document_id, {
        sessionId: payoutHint.session_id,
        payoutIndex: payoutHint.payout_index,
      });
      toast.success(
        `Factura vinculada al retiro de caja del ${payoutHint.session_date}. Gasto placeholder eliminado.`
      );
      setPayoutHint(null);
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "No se pudo vincular");
    } finally {
      setLinkingPayout(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Revisar documento — {doc.filename}
          </DialogTitle>
        </DialogHeader>

        {payoutHint && (
          <div
            className="rounded-lg border border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-3 flex items-start gap-3"
            data-testid="cash-payout-match-banner"
          >
            <div className="mt-0.5 h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle className="h-4 w-4 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-emerald-900 text-sm">
                ¿Esta factura corresponde a un retiro de caja?
              </div>
              <div className="text-xs text-emerald-800/90 mt-0.5">
                Detectamos un retiro del <strong>{payoutHint.session_date}</strong> de{" "}
                <strong>{Number(payoutHint.amount).toFixed(2)}€</strong> a{" "}
                <strong>{payoutHint.paid_to || payoutHint.concept}</strong>{" "}
                sin factura asociada. Al vincular se eliminará el gasto
                placeholder y se usará esta factura como documento oficial.
              </div>
              <div className="text-[10px] text-emerald-700/80 mt-1">
                Coincidencia {payoutHint.match_score}% · Diferencia de importe {payoutHint.amount_diff_pct || 0}%
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button
                size="sm" variant="outline"
                onClick={() => setPayoutHint(null)}
                data-testid="payout-link-dismiss"
              >
                Ignorar
              </Button>
              <Button
                size="sm"
                onClick={linkPayout}
                disabled={linkingPayout}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="payout-link-confirm"
              >
                {linkingPayout ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Vincular"}
              </Button>
            </div>
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-lg overflow-hidden border bg-muted/50 max-h-[500px] relative">
            <img
              key={previewUrl}
              src={previewUrl}
              alt={`Vista previa ${doc.filename}`}
              className="w-full object-contain max-h-[500px] bg-card"
              data-testid="doc-preview-img"
              onError={(e) => { e.target.style.opacity = 0.3; }}
            />
            {isPdf && pageCount > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-card/95 border rounded-full shadow-sm px-2 py-1 text-xs">
                <button
                  type="button"
                  className="px-2 py-0.5 rounded hover:bg-gray-100 disabled:opacity-40"
                  onClick={() => setPreviewPage(p => Math.max(0, p - 1))}
                  disabled={previewPage === 0}
                  data-testid="doc-preview-prev"
                >‹</button>
                <span className="text-foreground tabular-nums">
                  Página {previewPage + 1} / {pageCount}
                </span>
                <button
                  type="button"
                  className="px-2 py-0.5 rounded hover:bg-gray-100 disabled:opacity-40"
                  onClick={() => setPreviewPage(p => Math.min(pageCount - 1, p + 1))}
                  disabled={previewPage >= pageCount - 1}
                  data-testid="doc-preview-next"
                >›</button>
              </div>
            )}
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs bg-card/95 hover:bg-white border rounded-md px-2 py-1 shadow-sm text-foreground hover:text-primary transition"
              data-testid="doc-preview-open"
            >
              <Eye className="h-3 w-3" /> Abrir original
            </a>
          </div>
          <div className="space-y-3">
            {doc.ocr_confidence != null && (
              <div className={`text-xs rounded-lg px-3 py-2 ${doc.ocr_confidence < 0.8 ? "bg-orange-50 text-orange-900 border border-orange-200" : "bg-emerald-50 text-emerald-900 border border-emerald-200"}`}>
                <Sparkles className="h-3 w-3 inline mr-1" />
                Confianza IA: <strong>{(doc.ocr_confidence * 100).toFixed(0)}%</strong> · {doc.ocr_provider || "—"}
                {doc.ocr_confidence < 0.8 && " · revisa con atención"}
              </div>
            )}
            <div className="space-y-2">
              <Label>Proveedor / Emisor</Label>
              <Input value={form.supplier_name} onChange={(e) => setForm(f => ({ ...f, supplier_name: e.target.value }))} data-testid="review-supplier" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Fecha emisión</Label>
                <Input type="date" value={form.issue_date} onChange={(e) => setForm(f => ({ ...f, issue_date: e.target.value }))} data-testid="review-date" />
              </div>
              <div className="space-y-2">
                <Label>Vencimiento</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Total (€)</Label>
                <Input type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm(f => ({ ...f, total_amount: e.target.value }))} data-testid="review-total" />
              </div>
              <div className="space-y-2">
                <Label>IVA (€)</Label>
                <Input type="number" step="0.01" value={form.tax_amount} onChange={(e) => setForm(f => ({ ...f, tax_amount: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="review-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(categories || []).map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.paid} onChange={(e) => setForm(f => ({ ...f, paid: e.target.checked }))} />
              Marcar como pagado
            </label>

            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.is_recurring}
                  onChange={(e) => setForm(f => ({ ...f, is_recurring: e.target.checked }))}
                  className="h-4 w-4 rounded border-border text-violet-600 focus:ring-violet-500"
                  data-testid="review-recurring-checkbox"
                />
                <Repeat className="h-3.5 w-3.5 text-violet-600" />
                <span className="text-sm font-medium text-violet-900">
                  Esta factura es recurrente (aprender patrón)
                </span>
              </label>
              {form.is_recurring && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Frecuencia</Label>
                    <Select
                      value={form.recurring_cadence}
                      onValueChange={(v) => setForm(f => ({ ...f, recurring_cadence: v }))}
                    >
                      <SelectTrigger data-testid="review-recurring-cadence"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Mensual</SelectItem>
                        <SelectItem value="bimonthly">Bimestral</SelectItem>
                        <SelectItem value="quarterly">Trimestral</SelectItem>
                        <SelectItem value="biannual">Semestral</SelectItem>
                        <SelectItem value="annual">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Día del mes</Label>
                    <Input
                      type="number" min={1} max={31}
                      value={form.recurring_day}
                      onChange={(e) => setForm(f => ({ ...f, recurring_day: e.target.value }))}
                      placeholder={form.issue_date ? form.issue_date.slice(8, 10) : "1"}
                      data-testid="review-recurring-day"
                    />
                  </div>
                  <p className="col-span-2 text-[11px] text-violet-700/80">
                    Al confirmar, este proveedor quedará en tu previsión de caja y te
                    avisaremos si no llega la siguiente factura a tiempo.
                  </p>
                </div>
              )}
            </div>

            {/* P&L impact preview */}
            {form.total_amount && Number(form.total_amount) > 0 && (
              <InvoiceImpactPanel
                restaurantId={restaurantId}
                documentId={doc.document_id}
              />
            )}

            {canSync && (
              <div
                className={`rounded-lg border p-3 text-xs space-y-2 ${alreadySynced ? "bg-muted/50 border-border text-foreground" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}
                data-testid="inventory-sync-cta"
              >
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 shrink-0" />
                  <strong>
                    {alreadySynced
                      ? "Inventario ya actualizado con esta factura"
                      : `Este documento tiene ${items.length} producto${items.length === 1 ? "" : "s"} detectado${items.length === 1 ? "" : "s"}`}
                  </strong>
                </div>
                <p className="opacity-80">
                  {alreadySynced
                    ? "Los movimientos de stock ya se registraron. Puedes volver a aplicar si es necesario."
                    : "Puedo darte de alta los que falten y sumar el stock de los que ya tienes."}
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant={alreadySynced ? "outline" : "default"}
                  onClick={() => setSyncOpen(true)}
                  className={alreadySynced ? "" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                  data-testid="open-sync-inventory-btn"
                >
                  <Package className="h-3.5 w-3.5 mr-1.5" />
                  {alreadySynced ? "Volver a sincronizar" : "Actualizar inventario con esta factura"}
                </Button>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={reanalyze}
                disabled={reanalyzing || saving}
                className="border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                data-testid="review-reanalyze"
                title="Guarda tus correcciones como patrón del proveedor y reejecuta la IA"
              >
                {reanalyzing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-1.5" />
                )}
                Reanalizar con IA
              </Button>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={onClose}>Cancelar</Button>
                <Button onClick={() => save("processed")} disabled={saving || reanalyzing} data-testid="review-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-1.5" /> Confirmar y guardar</>}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {syncOpen && (
          <SyncToInventoryDialog
            open={syncOpen}
            onClose={() => setSyncOpen(false)}
            restaurantId={restaurantId}
            documentId={doc.document_id}
            onSynced={() => { onSaved?.(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


function PendingReceiptsWidget({ items, totalAmount, onPickFile }) {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;
  const visible = expanded ? items : items.slice(0, 3);
  const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR",
  }).format(n || 0);
  return (
    <div
      className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4"
      data-testid="pending-receipts-widget"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
            <FileText className="h-4 w-4 text-amber-700" />
          </div>
          <div>
            <h4 className="font-semibold text-amber-900 text-sm">
              {items.length} factura{items.length === 1 ? "" : "s"} pendiente{items.length === 1 ? "" : "s"} de subir
            </h4>
            <p className="text-[11px] text-amber-800/80">
              Retiros de caja de los últimos 30 días sin justificante físico · Total {fmtEUR(totalAmount)}
            </p>
          </div>
        </div>
        {items.length > 3 && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-[11px] text-amber-800 underline underline-offset-2 hover:text-amber-900"
            data-testid="toggle-pending-receipts"
          >
            {expanded ? "Ver menos" : `Ver ${items.length - 3} más`}
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {visible.map(p => (
          <div
            key={`${p.session_id}-${p.payout_index}`}
            className="flex items-center gap-2 bg-card/80 border border-amber-200/70 rounded-md px-2.5 py-1.5"
            data-testid={`pending-receipt-${p.session_id}-${p.payout_index}`}
          >
            <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-foreground truncate max-w-[220px]">
                {p.paid_to || p.concept}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {p.concept && p.paid_to && p.concept !== p.paid_to ? `· ${p.concept}` : ""}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200">
                {p.session_date} · {p.days_ago === 0 ? "hoy" : `hace ${p.days_ago}d`}
              </span>
            </div>
            <div className="text-sm font-bold tabular-nums">{fmtEUR(p.amount)}</div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onPickFile?.(p)}
              className="h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid={`upload-pending-${p.session_id}-${p.payout_index}`}
            >
              <Upload className="h-3 w-3 mr-1" /> Subir factura
            </Button>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-amber-700/70 mt-2">
        Al subir la factura, la IA la vinculará automáticamente al retiro y sustituirá el gasto placeholder.
      </p>
    </div>
  );
}
